import { useSearchParams } from "react-router-dom";
import { Mail, MapPin, MessageCircle, Phone } from "lucide-react";
import PageHead from "../components/PageHead";
import { Reveal } from "../components/Reveal";
import QuoteForm from "../components/QuoteForm";
import { DEFAULT_WA, SITE, waLink } from "../lib/site";

export default function Contact() {
    const [params] = useSearchParams();
    const size = params.get("size") || "";

    return (
        <>
            <PageHead
                eyebrow="Contact Us"
                title="Let's talk freight"
                hindi="Reach us anytime — we respond fast"
                intro="Call, WhatsApp or fill the quote form — we respond fast, with a competitive rate and a trailer that shows up."
                testid="contact-page"
            />

            <section className="pb-20 lg:pb-28" data-testid="contact-section">
                <div className="container-x grid gap-10 lg:grid-cols-12">
                    <div className="space-y-4 lg:col-span-5">
                        {SITE.phones.map((p, i) => (
                            <Reveal key={p.tel} delay={i * 0.05}>
                                <a
                                    href={`tel:${p.tel}`}
                                    data-testid={`contact-phone-link-${i + 1}`}
                                    className="card flex items-center gap-4 p-5 transition-all hover:-translate-y-0.5 hover:border-ink"
                                >
                                    <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-brand/10">
                                        <Phone className="h-5 w-5 text-brand" />
                                    </span>
                                    <span>
                                        <span className="block text-xs font-semibold uppercase tracking-[0.15em] text-fog">
                                            Call {i === 0 ? "Anand" : "our team"}
                                        </span>
                                        <span className="mt-0.5 block font-display text-lg font-bold tracking-tight text-ink">
                                            +91 {p.display}
                                        </span>
                                    </span>
                                </a>
                            </Reveal>
                        ))}

                        <Reveal delay={0.1}>
                            <a
                                href={`mailto:${SITE.email}`}
                                data-testid="contact-email-link"
                                className="card flex items-center gap-4 p-5 transition-all hover:-translate-y-0.5 hover:border-ink"
                            >
                                <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-brand/10">
                                    <Mail className="h-5 w-5 text-brand" />
                                </span>
                                <span>
                                    <span className="block text-xs font-semibold uppercase tracking-[0.15em] text-fog">
                                        Email
                                    </span>
                                    <span className="mt-0.5 block break-all font-display text-lg font-bold tracking-tight text-ink">
                                        {SITE.email}
                                    </span>
                                </span>
                            </a>
                        </Reveal>

                        <Reveal delay={0.15}>
                            <a
                                href={SITE.mapsUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                data-testid="contact-address-link"
                                className="card flex items-start gap-4 p-5 transition-all hover:-translate-y-0.5 hover:border-ink"
                            >
                                <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-brand/10">
                                    <MapPin className="h-5 w-5 text-brand" />
                                </span>
                                <span>
                                    <span className="block text-xs font-semibold uppercase tracking-[0.15em] text-fog">
                                        Office
                                    </span>
                                    <span className="mt-0.5 block text-sm font-semibold leading-relaxed text-ink">
                                        {SITE.address}
                                    </span>
                                    <span className="mt-1 block text-xs font-semibold text-brand">
                                        Open in Google Maps
                                    </span>
                                </span>
                            </a>
                        </Reveal>

                        <Reveal delay={0.2}>
                            <a
                                href={DEFAULT_WA}
                                target="_blank"
                                rel="noopener noreferrer"
                                data-testid="whatsapp-enquiry-btn"
                                className="flex items-center gap-4 rounded-2xl bg-[#1FA855] p-5 text-white transition-all hover:-translate-y-0.5 hover:bg-[#178a45] hover:shadow-lg hover:shadow-[#1FA855]/25"
                            >
                                <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-white/15">
                                    <MessageCircle className="h-5 w-5" />
                                </span>
                                <span>
                                    <span className="block text-xs font-semibold uppercase tracking-[0.15em] text-white/75">
                                        Fastest response
                                    </span>
                                    <span className="mt-0.5 block font-display text-lg font-bold tracking-tight">
                                        WhatsApp Enquiry
                                    </span>
                                    <span className="mt-0.5 block text-xs text-white/75">
                                        +91 {SITE.phones[0].display} — instant response
                                    </span>
                                </span>
                            </a>
                        </Reveal>
                    </div>

                    <div id="quote" className="lg:col-span-7" data-testid="quote-section">
                        <Reveal delay={0.1}>
                            <div className="mb-6">
                                <p className="eyebrow">Get a Trailer Quote</p>
                                <h2 className="mt-3 font-display text-2xl font-extrabold uppercase tracking-tight text-ink sm:text-3xl">
                                    Tell us the lane — we'll call back with a rate
                                </h2>
                                <p className="mt-2 font-hindi text-base text-steel">
                                    Fill the form — we'll call back with a rate shortly.
                                </p>
                            </div>
                            <QuoteForm initialSize={size} />
                        </Reveal>
                    </div>
                </div>
            </section>
        </>
    );
}
